import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, Download, Clock, AlertTriangle } from "lucide-react";

const API_ROOT = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SharedReport() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const resp = await axios.get(`${API_ROOT}/reports/share/${token}`);
        setData(resp.data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Link unavailable");
      } finally { setLoading(false); }
    })();
  }, [token]);

  const download = async () => {
    const resp = await axios.get(`${API_ROOT}/reports/share/${token}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(resp.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `altiora-shared-${token}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F7F3] text-[#1F2937] flex items-center justify-center font-mono text-xs uppercase tracking-widest text-[#5B5B62]">
        Loading…
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F8F7F3] text-[#1F2937] flex items-center justify-center">
        <div className="max-w-md text-center px-6">
          <AlertTriangle className="h-8 w-8 text-[#D97706] mx-auto" strokeWidth={1.5} />
          <div className="mt-4 font-display text-xl">Lien indisponible</div>
          <div className="mt-2 text-sm text-[#5B5B62] font-mono">{error}</div>
        </div>
      </div>
    );
  }

  const p = data.payload || {};

  return (
    <div className="min-h-screen bg-[#F8F7F3] text-[#1F2937]" data-testid="shared-report-root">
      <div className="border-b border-[#E4E1D8] px-6 md:px-10 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ShieldCheck className="h-5 w-5 text-[#0B2545]" strokeWidth={1.8} />
          <div className="text-xs font-mono uppercase tracking-[0.24em] text-[#5B5B62]">
            ALTIORA · Secure share
          </div>
        </div>
        <div className="flex items-center gap-4 text-[10px] font-mono text-[#5B5B62]">
          <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> exp. {new Date(data.expires_at).toLocaleString()}</span>
          <span>{data.downloads_left} left</span>
        </div>
      </div>

      <div className="px-6 md:px-10 py-10 max-w-4xl mx-auto space-y-8">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#0B2545] mb-2">
            {data.report_type}
          </div>
          <h1 className="font-display text-4xl tracking-tight text-[#1F2937]">{data.title}</h1>
          <div className="mt-2 text-sm text-[#5B5B62]">
            {p.executive_summary}
          </div>
        </div>

        {data.pdf_ready && (
          <button
            onClick={download}
            data-testid="shared-report-download"
            className="inline-flex items-center gap-2 bg-[#0B2545] hover:bg-[#DCC29A] text-white text-sm font-medium px-5 py-2.5 rounded-md transition-colors"
          >
            <Download className="h-4 w-4" strokeWidth={1.8} />
            Télécharger le PDF
          </button>
        )}

        {p.score && (
          <div className="rounded-md border border-[#E4E1D8] bg-white p-6">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#5B5B62] mb-2">Score ALTIORA</div>
            <div className="font-mono text-5xl text-[#0B2545]">{p.score.score ?? "—"}</div>
            <div className="mt-1 text-xs text-[#5B5B62]">{p.score.band}</div>
          </div>
        )}

        {p.explanation && (
          <div className="rounded-md border border-[#E4E1D8] bg-white p-6">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#0B2545] mb-4">Trusted Intelligence Framework™</div>
            <div className="space-y-3 text-sm">
              <Row label="Pourquoi" value={p.explanation.why} />
              <Row label="Données utilisées" value={(p.explanation.data_used || []).join(", ")} />
              <Row label="Hypothèses" value={(p.explanation.assumptions || []).join(", ")} />
              <Row label="Confiance" value={`${Math.round((p.explanation.confidence || 0) * 100)}%`} />
              <Row label="Actions pour améliorer" value={(p.explanation.improve_by || []).join(", ")} />
              <Row label="Aide à la décision uniquement" value="Oui — jamais une garantie." accent />
            </div>
          </div>
        )}

        {(p.disclaimers || []).length > 0 && (
          <div className="text-[10px] text-[#5B5B62] font-mono leading-relaxed">
            {p.disclaimers.map((d, i) => <div key={i} className="mb-1">{d}</div>)}
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value, accent }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4">
      <div className="text-[10px] font-mono uppercase tracking-widest text-[#5B5B62] sm:w-52">{label}</div>
      <div className={`text-sm ${accent ? "text-[#0B2545]" : "text-[#1F2937]"}`}>{value || "—"}</div>
    </div>
  );
}
